<template>
  <div>
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-2xl font-bold text-gray-900">Dashboard</h1>
      <p class="text-gray-600 mt-1">
        Bem-vindo, <span class="font-medium">{{ authStore.user?.full_name || 'Admin' }}</span>!
      </p>
    </div>
    
    <!-- Stats Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div class="flex items-center">
          <div class="p-3 rounded-xl bg-blue-100 text-blue-600">
            <ShoppingBagIcon class="h-6 w-6" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Pedidos Hoje</p>
            <p class="text-2xl font-bold text-gray-900">{{ stats.today_orders || 0 }}</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div class="flex items-center">
          <div class="p-3 rounded-xl bg-green-100 text-green-600">
            <CurrencyDollarIcon class="h-6 w-6" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Receita Hoje</p>
            <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(stats.today_revenue || 0) }}</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div class="flex items-center">
          <div class="p-3 rounded-xl bg-yellow-100 text-yellow-600">
            <ClockIcon class="h-6 w-6" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Pendentes</p>
            <p class="text-2xl font-bold text-gray-900">{{ stats.pending_orders || 0 }}</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div class="flex items-center">
          <div class="p-3 rounded-xl bg-purple-100 text-purple-600">
            <FireIcon class="h-6 w-6" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Em Preparo</p>
            <p class="text-2xl font-bold text-gray-900">{{ stats.preparing_orders || 0 }}</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Quick Actions -->
    <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8">
      <h2 class="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h2>
      <div class="flex flex-wrap gap-4">
        <router-link
          to="/admin/orders"
          class="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <ShoppingBagIcon class="h-5 w-5 mr-2" />
          Ver Pedidos
        </router-link>
        <router-link
          to="/admin/products"
          class="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          <CubeIcon class="h-5 w-5 mr-2" />
          Produtos
        </router-link>
        <router-link
          to="/admin/categories"
          class="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          <TagIcon class="h-5 w-5 mr-2" />
          Categorias
        </router-link>
        <router-link
          to="/admin/delivery"
          class="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          <TruckIcon class="h-5 w-5 mr-2" />
          Entrega
        </router-link>
      </div>
    </div>
    
    <!-- Recent Orders -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100">
      <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <h2 class="text-lg font-semibold text-gray-900">Pedidos Recentes</h2>
        <router-link
          to="/admin/orders"
          class="text-orange-600 hover:text-orange-700 text-sm font-medium"
        >
          Ver todos
        </router-link>
      </div>
      
      <div v-if="loading" class="p-8 text-center text-gray-500">
        Carregando...
      </div>
      
      <div v-else-if="recentOrders.length === 0" class="p-8 text-center text-gray-500">
        <ShoppingBagIcon class="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p>Nenhum pedido encontrado</p>
      </div>
      
      <!-- Modal de Detalhes do Pagamento -->
      <div v-if="showPaymentModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" @click.self="closePaymentModal">
        <div class="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-bold text-gray-900">Detalhes do Pagamento</h3>
            <button @click="closePaymentModal" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <div v-if="selectedOrder" class="space-y-4">
            <!-- Info do Pedido -->
            <div class="bg-gray-50 p-4 rounded-lg">
              <p class="text-sm text-gray-600">Pedido: <span class="font-medium text-gray-900">#{{ selectedOrder.order_number }}</span></p>
              <p class="text-sm text-gray-600">Cliente: <span class="font-medium text-gray-900">{{ selectedOrder.customer_name || selectedOrder.customer?.name || 'N/A' }}</span></p>
              <p class="text-sm text-gray-600">Total: <span class="font-medium text-gray-900">{{ formatCurrency(selectedOrder.total || 0) }}</span></p>
            </div>
            
            <!-- Forma de Pagamento -->
            <div>
              <label class="block text-xs font-medium text-gray-500 uppercase mb-1">Forma de Pagamento</label>
              <p class="text-lg font-medium text-gray-900">
                {{ getPaymentMethodLabel(selectedOrder.payment_method) }}
              </p>
            </div>
            
            <!-- Status do Pagamento -->
            <div>
              <label class="block text-xs font-medium text-gray-500 uppercase mb-1">Status do Pagamento</label>
              <div class="flex items-center gap-3">
                <span 
                  class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                  :class="getPaymentStatusClass(selectedOrder.payment_status)"
                >
                  {{ getPaymentStatusLabel(selectedOrder.payment_status) }}
                </span>
                <button
                  v-if="selectedOrder.payment_external_id"
                  @click="refreshPaymentStatus(selectedOrder.id)"
                  :disabled="refreshing"
                  class="text-sm text-orange-600 hover:text-orange-800 font-medium flex items-center gap-1"
                >
                  <svg v-if="refreshing" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                  {{ refreshing ? 'Atualizando...' : 'Atualizar' }}
                </button>
              </div>
            </div>
            
            <!-- ID Externo (Mercado Pago) -->
            <div v-if="selectedOrder.payment_external_id">
              <label class="block text-xs font-medium text-gray-500 uppercase mb-1">ID Mercado Pago</label>
              <p class="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">{{ selectedOrder.payment_external_id }}</p>
            </div>
            
            <!-- PIX Code (se for PIX) -->
            <div v-if="selectedOrder.payment_method === 'pix' && selectedOrder.payment_pix_code">
              <label class="block text-xs font-medium text-gray-500 uppercase mb-1">Código PIX</label>
              <div class="flex gap-2">
                <code class="flex-1 text-xs break-all bg-gray-100 px-2 py-1 rounded">{{ selectedOrder.payment_pix_code }}</code>
                <button 
                  @click="copyPixCode(selectedOrder.payment_pix_code)"
                  class="text-orange-600 hover:text-orange-800 text-sm font-medium"
                >
                  Copiar
                </button>
              </div>
            </div>
            
            <!-- Mensagem quando não há pagamento externo -->
            <div v-if="!selectedOrder.payment_external_id && selectedOrder.payment_method === 'cash'" class="bg-blue-50 p-4 rounded-lg">
              <p class="text-sm text-blue-700">
                <svg class="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Pagamento em dinheiro. Aguardar pagamento na entrega.
              </p>
            </div>
            
            <div v-else-if="!selectedOrder.payment_external_id" class="bg-yellow-50 p-4 rounded-lg">
              <p class="text-sm text-yellow-700">
                <svg class="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
                Pagamento ainda não processado no Mercado Pago.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else class="overflow-x-auto">
        <table class="w-full table-fixed divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Pedido</th>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-44">Cliente</th>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Total</th>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Pagamento</th>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Status</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="order in recentOrders" :key="order.id" class="hover:bg-gray-50">
              <td class="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                #{{ order.order_number }}
              </td>
              <td class="px-3 py-3 whitespace-nowrap text-sm text-gray-500 max-w-[160px] truncate" :title="order.customer_name || order.customer?.name || 'N/A'">
                {{ order.customer_name || order.customer?.name || 'N/A' }}
              </td>
              <td class="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ formatCurrency(order.total || 0) }}
              </td>
              <!-- Coluna Pagamento -->
              <td class="px-3 py-3 whitespace-nowrap text-sm">
                <button
                  v-if="order.payment_status && order.payment_status !== 'pending'"
                  @click="openPaymentModal(order)"
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors hover:opacity-80"
                  :class="getPaymentStatusClass(order.payment_status)"
                >
                  {{ getPaymentStatusLabel(order.payment_status) }}
                </button>
                <button
                  v-else
                  @click="openPaymentModal(order)"
                  class="text-orange-600 hover:text-orange-800 text-xs font-medium underline"
                >
                  Aguardando
                </button>
              </td>
              <td class="px-3 py-3 whitespace-nowrap">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="getStatusClass(order.status)"
                >
                  {{ getStatusLabel(order.status) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import {
  ShoppingBagIcon,
  CurrencyDollarIcon,
  ClockIcon,
  FireIcon,
  CubeIcon,
  TagIcon,
  TruckIcon
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores'
import { orderApi, paymentApi } from '@/utils/api'
import { formatCurrency } from '@/utils/formatters'
import { notifyError, notifySuccess } from '@/utils/notifications'
import type { Order } from '@/types'

const authStore = useAuthStore()

const loading = ref(true)
const stats = ref({
  today_orders: 0,
  today_revenue: 0,
  pending_orders: 0,
  preparing_orders: 0
})

const recentOrders = ref<Order[]>([])

// Modal de pagamento
const showPaymentModal = ref(false)
const selectedOrder = ref<Order | null>(null)
const refreshing = ref(false)

const getStatusClass = (status: string) => {
  const classes: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-purple-100 text-purple-800',
    ready: 'bg-indigo-100 text-indigo-800',
    out_for_delivery: 'bg-orange-100 text-orange-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    pending: 'Pendente',
    confirmed: 'Confirmado',
    preparing: 'Em Preparo',
    ready: 'Pronto',
    out_for_delivery: 'Em Entrega',
    delivered: 'Entregue',
    cancelled: 'Cancelado'
  }
  return labels[status] || status
}

// Funções de pagamento
const getPaymentStatusClass = (status: string | undefined) => {
  const classes: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-600',
    processing: 'bg-blue-100 text-blue-800',
    paid: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    refunded: 'bg-orange-100 text-orange-800'
  }
  return classes[status || 'pending'] || 'bg-gray-100 text-gray-600'
}

const getPaymentStatusLabel = (status: string | undefined) => {
  const labels: Record<string, string> = {
    pending: 'Pendente',
    processing: 'Processando',
    paid: 'Pago',
    failed: 'Falhou',
    refunded: 'Reembolsado'
  }
  return labels[status || 'pending'] || 'Pendente'
}

const getPaymentMethodLabel = (method: string | undefined) => {
  const labels: Record<string, string> = {
    credit_card: 'Cartão de Crédito',
    debit_card: 'Cartão de Débito',
    pix: 'PIX',
    cash: 'Dinheiro'
  }
  return labels[method || ''] || method || 'Não informado'
}

const openPaymentModal = (order: Order) => {
  selectedOrder.value = order
  showPaymentModal.value = true
}

const closePaymentModal = () => {
  showPaymentModal.value = false
  selectedOrder.value = null
}

const refreshPaymentStatus = async (orderId: number) => {
  refreshing.value = true
  try {
    const response = await paymentApi.refreshStatus(orderId)
    if (response.data.success) {
      // Atualiza o pedido na lista
      const index = recentOrders.value.findIndex(o => o.id === orderId)
      if (index !== -1) {
        recentOrders.value[index].payment_status = response.data.new_status
      }
      // Atualiza o pedido selecionado
      if (selectedOrder.value) {
        selectedOrder.value.payment_status = response.data.new_status
      }
      notifySuccess('Status atualizado com sucesso!')
    } else {
      notifyError(response.data.message || 'Não foi possível atualizar o status')
    }
  } catch (error: any) {
    notifyError('Erro ao atualizar status: ' + (error.response?.data?.detail || 'Erro desconhecido'))
  } finally {
    refreshing.value = false
  }
}

const copyPixCode = (code: string) => {
  navigator.clipboard.writeText(code)
  notifySuccess('Código PIX copiado!')
}

const loadData = async () => {
  loading.value = true
  const companyId = authStore.companyId || 1
  
  try {
    const statsRes = await orderApi.getStats(companyId)
    if (statsRes.data) {
      stats.value = statsRes.data
    }
    
    const ordersRes = await orderApi.getByCompany(companyId, { limit: 5 })
    if (ordersRes.data) {
      recentOrders.value = ordersRes.data
    }
  } catch (error) {
    console.error('Erro ao carregar dados:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadData()
})
</script>
